import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where 
} from 'firebase/firestore';

export const useSubscription = () => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user has active subscription
  useEffect(() => {
    console.log('🔍 useSubscription: Checking subscription status', {
      user: user?.id,
      isAuthenticated
    });

    if (!user || !isAuthenticated) {
      console.log('❌ useSubscription: No user or not authenticated');
      setSubscription(null);
      setLoading(false);
      return;
    }

    console.log('📡 useSubscription: Setting up subscription listener for user:', user.id);

    // Listen to user's subscriptions in Firestore
    const subscriptionsRef = collection(db, `customers/${user.id}/subscriptions`);
    const q = query(
      subscriptionsRef, 
      where('status', 'in', ['trialing', 'active'])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('📊 useSubscription: Snapshot received', {
          empty: snapshot.empty,
          size: snapshot.size
        });

        if (snapshot.empty) {
          console.log('❌ useSubscription: No active subscription found');
          setSubscription(null);
        } else {
          // User has active subscription
          const subData = snapshot.docs[0].data();
          console.log('✅ useSubscription: Active subscription found!', {
            id: snapshot.docs[0].id,
            status: subData.status
          });
          setSubscription({
            id: snapshot.docs[0].id,
            ...subData
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('❌ useSubscription: Subscription check error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('🔌 useSubscription: Unsubscribing from subscription listener');
      unsubscribe();
    };
  }, [user, isAuthenticated]);

  // Create Stripe Checkout session
  const createCheckoutSession = async (priceId, billingCycle) => {
    if (!user) {
      throw new Error('User must be logged in to subscribe');
    }

    setLoading(true);
    setError(null);

    try {
      // Create checkout session document in Firestore
      // The Stripe extension will automatically create the checkout URL
      const checkoutSessionRef = collection(
        db,
        `customers/${user.id}/checkout_sessions`
      );

      const docRef = await addDoc(checkoutSessionRef, {
        price: priceId,
        success_url: window.location.origin + '/?payment=success',
        cancel_url: window.location.origin + '/?payment=cancelled',
        allow_promotion_codes: true,
        metadata: {
          userId: user.id,
          email: user.email,
          userName: user.name,
          billingCycle: billingCycle
        }
      });

      // Wait for Stripe Checkout URL to be created by the extension
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data();
          
          if (data?.error) {
            unsubscribe();
            reject(new Error(data.error.message));
          }
          
          if (data?.url) {
            unsubscribe();
            resolve(data.url);
          }
        });

        // Timeout after 30 seconds
        setTimeout(() => {
          unsubscribe();
          reject(new Error('Checkout session creation timed out'));
        }, 30000);
      });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if user has premium access
  const hasPremiumAccess = () => {
    return subscription && subscription.status === 'active';
  };

  return {
    subscription,
    loading,
    error,
    hasPremiumAccess: hasPremiumAccess(),
    createCheckoutSession
  };
};
